import React, { FC } from 'react';

import { Control, useFormContext, useWatch } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { MIN_USD_PRICE } from 'constants/constants';
import { EPopupSubType } from 'constants/enums';

import { RadioButtonOptionsProps } from 'components/RadioButton/RadioButton';

import Checkbox from '../../../../components/Checkbox/Checkbox';
import Input from '../../../../components/Input/Input';
import InputSelectDropdown, {
  InputSelectDropdownOption
} from '../../../../components/InputSelectDropdown/InputSelectDropdown';
import RadioButtonGroup from '../../../../components/RadioButton/RadioButtonGroup';
import RichText from '../../../../components/RichText/RichText';
import Textarea from '../../../../components/Textarea/Textarea';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from '../../../../components/ui';
import { getPriorityOptions, PopupsFormFields } from '../constants';

type OfferDesignOptionsProps = {
  externalId: string;
  label: string;
  value: string;
};

type GeneralFieldsProps = {
  control: Control<PopupsFormFields>;
  currentOfferUiId: string;
  currentPriority: number;
  isDailyBonus: boolean;
  offerDesignOptions: OfferDesignOptionsProps[];
  onSubTypeChange?: (newSubType: EPopupSubType) => void;
  hasFeatureFlagPostPurchase: boolean;
  isNameTaken?: boolean;
  isPublisherOfferIdTaken?: boolean;
};

const GeneralSection: FC<GeneralFieldsProps> = ({
  control,
  currentOfferUiId,
  currentPriority,
  isDailyBonus,
  offerDesignOptions,
  onSubTypeChange,
  hasFeatureFlagPostPurchase,
  isNameTaken,
  isPublisherOfferIdTaken
}) => {
  const { setValue, formState } = useFormContext<PopupsFormFields>();

  const isFree = useWatch({ control, name: 'setAsFree' });

  const POPUPS_FORM_TYPE_OPTIONS: RadioButtonOptionsProps[] = [
    {
      value: EPopupSubType.DAILY_BONUS,
      id: EPopupSubType.DAILY_BONUS,
      label: 'Daily Bonus'
    },
    ...(hasFeatureFlagPostPurchase
      ? [
          {
            value: EPopupSubType.POST_PURCHASE,
            id: EPopupSubType.POST_PURCHASE,
            label: 'Post Purchase'
          }
        ]
      : [])
  ];

  const handleCheckboxChange = (checked: boolean) => {
    setValue('setAsFree', checked, {
      shouldDirty: true,
      shouldValidate: true
    });

    if (!isDailyBonus) {
      setValue('price', checked ? 0 : MIN_USD_PRICE, {
        shouldDirty: true,
        shouldValidate: true
      });
    }
  };

  return (
    <>
      <div>
        <RichText
          title="General"
          description="Choose your offer type"
          className="mb-5"
        />
        <FormField
          control={control}
          name="subType"
          render={({ field: { value, onChange } }) => (
            <FormItem>
              <FormControl>
                <RadioButtonGroup
                  value={value}
                  onValueChange={(option) => {
                    onChange(option);
                    onSubTypeChange?.(option as EPopupSubType);
                  }}
                  className="gap-x-9"
                  options={POPUPS_FORM_TYPE_OPTIONS}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="flex flex-col gap-5 pb-5 border-b border-base-sidebarBorder w-full">
        <div className="grid grid-cols-2 gap-5">
          <FormField
            control={control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    isRequired
                    label="Name"
                    placeholder="Name"
                    {...field}
                  />
                </FormControl>
                {formState.errors.name ? (
                  <FormMessage />
                ) : isNameTaken ? (
                  <FormMessage>Name already exists</FormMessage>
                ) : null}
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="displayName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    label="Display Name"
                    placeholder="Display Name"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea
                  label="Description"
                  placeholder="Write Something..."
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="grid grid-cols-2 gap-5">
          <FormField
            control={control}
            name="publisherOfferId"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    isRequired
                    label="External ID"
                    placeholder="External ID"
                    {...field}
                  />
                </FormControl>
                {formState.errors.publisherOfferId ? (
                  <FormMessage />
                ) : isPublisherOfferIdTaken ? (
                  <FormMessage>External ID already exists</FormMessage>
                ) : null}
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="offerUiId"
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <InputSelectDropdown
                    buttonDataTestId="popupDesignBox"
                    listDataTestId="dashPopupDesignList"
                    isRequired
                    key={value}
                    label="Offer Design"
                    defaultSelected={offerDesignOptions.find(
                      (option: InputSelectDropdownOption) =>
                        option.value === currentOfferUiId
                    )}
                    options={offerDesignOptions}
                    onOptionChange={onChange}
                    placeholder="Select Offer"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="priority"
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <InputSelectDropdown
                    key={value}
                    isRequired
                    label="Priority"
                    buttonDataTestId="popupPriorityBox"
                    listDataTestId="dashPopupPriorityList"
                    defaultSelected={getPriorityOptions().find(
                      (option) => option.value === currentPriority
                    )}
                    options={getPriorityOptions()}
                    onOptionChange={onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="price"
            render={({ field: { onChange, onBlur, value } }) => {
              const isPriceEditable = !isDailyBonus && !isFree;

              return (
                <FormItem>
                  <FormControl>
                    <>
                      <Input
                        name="price"
                        isRequired={!isFree}
                        label="Price"
                        leftIcon={
                          isPriceEditable ? (
                            <FontAwesomeIcon icon="fa-regular fa-dollar-sign" />
                          ) : undefined
                        }
                        type={isPriceEditable ? 'number' : 'text'}
                        step={1}
                        min={MIN_USD_PRICE}
                        value={isPriceEditable ? value ?? '' : 'Free'}
                        disabled={!isPriceEditable}
                        onChange={(e) => {
                          if (!isPriceEditable) return;
                          const parsed =
                            e.target.value === ''
                              ? null
                              : parseFloat(e.target.value);
                          onChange(parsed);
                        }}
                        onBlur={() => {
                          if (!isPriceEditable) return;
                          if (value == null || value < MIN_USD_PRICE) {
                            onChange(MIN_USD_PRICE);
                          }
                          onBlur();
                        }}
                      />
                      {!isDailyBonus && (
                        <FormField
                          control={control}
                          name="setAsFree"
                          render={({
                            field: { value: free, onChange: onFreeChange }
                          }) => (
                            <FormItem>
                              <FormControl>
                                <Checkbox
                                  checked={free}
                                  onCheckedChange={handleCheckboxChange}
                                  label="Set as free"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      )}
                    </>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>
      </div>
    </>
  );
};
export default GeneralSection;
